<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="devices"
      single-expand
      :expanded.sync="expanded"
      item-key="id"
      show-expand
      class="elevation-1"
      :loading="dataLoading"
      :mobile-breakpoint="0"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>Device Manager</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
      </template>
      <template v-slot:item.lastUpdate="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-chip
                :color="onlineColor(item.LastUpdateUnix, item.sleepSeconds)"
                dark
                small
                label
              >
                {{ onlineStatus(item.LastUpdateUnix, item.sleepSeconds) }}
              </v-chip></span
            >
          </template>
          <span>{{ computedTimeAgo(item.LastUpdateUnix) }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.items="{ item }">
        <v-chip
          v-if="item.sleepSeconds"
          class="mr-4 primary--text"
          color="primary lighten-4"
          dark
          small
          label
        >
          <v-icon left small>mdi-alarm-snooze</v-icon>
          {{ item.sleepSeconds }}s
        </v-chip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span class="mr-4" v-bind="attrs" v-on="on"
              ><v-icon>{{ chargeIcon(item.batteryPercent) }}</v-icon></span
            >
          </template>
          <span v-if="item.batteryPercent < 0">Plugged In</span>
          <span v-else
            >Charge: {{ item.batteryPercent
            }}{{ prefix(item.batteryPercent) }}</span
          >
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span class="mr-4" v-bind="attrs" v-on="on"
              ><v-icon>{{ wifiIcon(item.wifiSignal) }}</v-icon></span
            >
          </template>
          <span v-if="item.wifiSignal < 0">Ethernet</span>
          <span v-else
            >Signal: {{ item.wifiSignal }}{{ prefix(item.wifiSignal) }}</span
          >
        </v-tooltip>
        <v-chip
          v-if="item.WiFiSSID"
          color="primary lighten-4"
          class="primary--text"
          small
          label
        >
          <v-icon left small>mdi-wifi</v-icon>
          {{ item.WiFiSSID }}
        </v-chip>
      </template>
      <template v-slot:item.edit="{ item }">
        <EditModal :device="item" />
      </template>
      <template v-slot:item.data-table-expand="{ item, expand, isExpanded }">
        <v-btn
          color="primary"
          icon
          v-if="item.firmware !== null"
          @click="expand(!isExpanded)"
        >
          <v-icon>{{
            isExpanded ? "mdi-chevron-up" : "mdi-chevron-down"
          }}</v-icon>
        </v-btn>
      </template>
      <template v-slot:expanded-item="{ item }">
        <td :colspan="headers.length" style="background-color: #f8f9fa;">
          <v-simple-table
            v-if="item.firmware"
            style="background-color: #f8f9fa;"
          >
            <thead>
              <tr>
                <th class="text-left">
                  File Name
                </th>
                <th class="text-left">
                  Status
                </th>
                <th class="text-left">
                  File Uploaded
                </th>
                <th class="text-left">
                  Device Downloaded
                </th>
                <th class="text-left" width="120">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ item.firmware.fileName }}</td>
                <td>
                  <v-chip
                    :color="computedStatusColor(item.firmware.update)"
                    small
                    label
                    dark
                  >
                    {{ computedStatusText(item.firmware.update) }}
                    <v-progress-circular
                      v-if="computedStatusDld(item.firmware.update)"
                      :size="15"
                      :width="2"
                      color="white"
                      indeterminate
                      class="ml-2"
                    ></v-progress-circular>
                  </v-chip>
                </td>
                <td>{{ item.firmware.uploadDate }}</td>
                <td>{{ item.firmware.lastUpdate }}</td>
                <td>
                  <v-btn
                    x-small
                    color="primary"
                    class="mr-2"
                    @click.stop="editItem(item)"
                  >
                    <v-icon small>
                      mdi-file-upload-outline
                    </v-icon>
                  </v-btn>
                  <v-btn
                    x-small
                    color="danger"
                    dark
                    @click.stop="deleteItem(item)"
                  >
                    <v-icon small>
                      mdi-trash-can-outline
                    </v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </td>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog" max-width="490">
      <v-card flat>
        <v-card-title> File Upload - {{ editedItem.deviceName }} </v-card-title>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-card-text>
            <v-file-input
              :rules="rules"
              v-model="upload"
              accept=".bin"
              chips
              outlined
              dense
              color="primary"
              placeholder="Upload a file"
              prepend-icon="mdi-file-upload"
              label="Firmware"
            ></v-file-input>
          </v-card-text>
          <v-card-actions>
            <v-btn
              color="primary"
              @click="uploadDocument()"
              :loading="dataLoading"
            >
              Upload
            </v-btn>
            <v-btn @click="close">
              Close
            </v-btn></v-card-actions
          >
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";
import {
  computedOnlineStatusText,
  computedOnlineStatusColor,
} from "@/components/atoms/OnlineStatus.js";
import EditModal from "@/components/device/DeviceEditModal.vue";

export default {
  components: {
    EditModal,
  },
  data() {
    return {
      upload: null,
      valid: true,
      rules: [
        (v) => !!v || "Please select a file to upload",
        (value) =>
          !value ||
          value.size < 5000000 ||
          "File size should be less than 5 MB!",
      ],
      expanded: [],
      headers: [
        { text: "Device Name", value: "deviceName", width: "150" },
        { text: "Device ID", value: "id" },
        { text: "Last Updated", value: "lastUpdate" },
        { text: "Info", value: "items", width: "350" },
        { text: "", value: "edit", align: "center" },
        { text: "Firmware", value: "data-table-expand", align: "center" },
      ],
      dialog: false,
      editedItem: {},
    };
  },
  computed: {
    ...mapState({
      btnLoading: (state) => state.btnLoading,
      dataLoading: (state) => state.dataLoading,
      user: (state) => state.auth.user,
      devices: (state) => state.device.devices,
    }),
  },
  mounted() {
    this.getDevices();
  },
  methods: {
    getDevices() {
      this.$store.dispatch("fetchAllDevices");
    },

    computedTimeAgo(time) {
      return time ? moment.unix(time).fromNow() : "Unknown";
    },

    computedStatusColor(update) {
      switch (update) {
        case 1:
          return "warning";
        case 2:
          return "success";
        case 3:
          return "danger";
        default:
          return "accent lighten-4";
      }
    },

    computedStatusText(update) {
      switch (update) {
        case 1:
          return "Pending Download";
        case 2:
          return "Downloading Firmware";
        case 3:
          return "Download Error";
        case 4:
          return "Download Succeeded";
        default:
          return "Up to date";
      }
    },

    computedStatusDld(update) {
      switch (update) {
        case 2:
          return true;
        default:
          return false;
      }
    },

    onlineStatus(update, sleep) {
      return computedOnlineStatusText(update, sleep);
    },
    onlineColor(update, sleep) {
      return computedOnlineStatusColor(update, sleep);
    },

    computedOnlineStatusText(update, sleep) {
      const sleepTime = sleep + 60;
      const timeNow = moment().unix();
      const lastUpdate = Number(update) + Number(sleepTime);

      if (lastUpdate >= timeNow) {
        return "Online";
      } else {
        return "Offline";
      }
    },

    computedOnlineStatusColor(update, sleep) {
      const sleepTime = sleep + 60;
      const timeNow = moment().unix();
      const lastUpdate = Number(update) + Number(sleepTime);

      if (lastUpdate >= timeNow) {
        return "success";
      } else {
        return "danger";
      }
    },

    uploadDocument() {
      if (this.$refs.form.validate()) {
        const payload = {
          file: this.upload,
          name: this.upload.name,
          id: this.editedItem.id,
        };

        this.$store.dispatch("uploadFirmware", payload).then(() => {
          this.upload = null;
          this.dialog = false;
        });
      }
    },

    deleteItem(item) {
      this.$confirm(
        `Deleting ${item.deviceName} will also remove the device itself and all associated data including Chart Data.`,
        {
          title: `Delete ${item.deviceName}?`,
          icon: "",
          color: "danger",
          buttonTrueFlat: false,
          buttonFalseFlat: false,
          buttonTrueColor: "danger",
          buttonFalseColor: "",
          buttonTrueText: "Delete",
          buttonFalseText: "Cancel",
        }
      ).then((res) => {
        if (res === true) {
          const payload = {
            name: item.fileName,
            id: item.id,
          };
          this.$store.dispatch("deleteFirmware", payload);
        }
      });
    },

    editItem(item) {
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },

    close() {
      this.upload = null;
      this.dialog = false;
    },

    chargeIcon(charge) {
      if (charge >= 95) {
        return "mdi-battery mdi-rotate-90";
      } else if (charge >= 90) {
        return "mdi-battery-90 mdi-rotate-90";
      } else if (charge >= 80) {
        return "mdi-battery-80 mdi-rotate-90";
      } else if (charge >= 70) {
        return "mdi-battery-70 mdi-rotate-90";
      } else if (charge >= 60) {
        return "mdi-battery-60 mdi-rotate-90";
      } else if (charge >= 50) {
        return "mdi-battery-50 mdi-rotate-90";
      } else if (charge >= 40) {
        return "mdi-battery-40 mdi-rotate-90";
      } else if (charge >= 30) {
        return "mdi-battery-30 mdi-rotate-90";
      } else if (charge >= 20) {
        return "mdi-battery-20 mdi-rotate-90";
      } else if (charge >= 10) {
        return "mdi-battery-10 mdi-rotate-90";
      } else if (charge < 0) {
        return "mdi-power-plug";
      } else if (charge >= "Unknown") {
        return "mdi-battery-off-outline mdi-rotate-90";
      } else {
        return "mdi-battery-outline mdi-rotate-90";
      }
    },

    wifiIcon(wifiSignal) {
      if (wifiSignal === "Unknown") {
        return "mdi-wifi-strength-off-outline";
      } else if (wifiSignal >= 90) {
        return "mdi-wifi-strength-4";
      } else if (wifiSignal >= 75) {
        return "mdi-wifi-strength-3";
      } else if (wifiSignal >= 50) {
        return "mdi-wifi-strength-2";
      } else if (wifiSignal >= 10) {
        return "mdi-wifi-strength-1";
      } else if (wifiSignal < 0) {
        return "mdi-ethernet";
      } else {
        return "mdi-wifi-strength-outline";
      }
    },

    prefix(item) {
      if (item !== "Unknown") {
        return "%";
      } else {
        return "";
      }
    },
  },
};
</script>
